import { memo, useMemo, useCallback } from 'react';
import styled from '@emotion/styled';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { shouldForwardProp } from '@trinity/chat-components/utils/emotion';

type PaginationProps = {
  pageCount: number;
  pageIndex: number;
  goToPage: (pageIndex: number) => void;
};

export const Pagination = memo(({
  pageCount,
  pageIndex,
  goToPage,
}: PaginationProps) => {

  const canPreviousPage = pageIndex > 0;
  const canNextPage = pageIndex + 1 < pageCount;
  const previousPage = () => goToPage(pageIndex - 1);
  const nextPage = () => goToPage(pageIndex + 1);

  const pages = useMemo(() => {
    const pages: number[] = [];
    for (let i = 0; i < pageCount; i += 1) {
      pages.push(i);
    }
    return pages;
  }, [pageCount]);

  const jumpToPage = useCallback((page: number) => () => {
    goToPage(page);
  }, [goToPage]);

  return (
    <PaginationRoot>
      <StyledChevronLeft
        disabled={!canPreviousPage}
        onClick={previousPage} />
      <PagesRoot>
        {pages.map(page => (
          <StyledPage
            key={page}
            onClick={jumpToPage(page)}
            active={page === pageIndex}>
            {page + 1}
          </StyledPage>
        ))}
      </PagesRoot>
      <StyledChevronRight
        disabled={!canNextPage}
        onClick={nextPage} />
    </PaginationRoot>
  );
});

const PagesRoot = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
});

const PaginationRoot = styled.div(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: 10,
  marginTop: 15,
}));


const StyledChevronLeft = styled(ChevronLeft, { shouldForwardProp: shouldForwardProp('disabled') })<ChevronProps>(({ disabled, theme }) => ({
  cursor: disabled ? 'default' : 'pointer',
  color: disabled ? theme.palette.gray.light1 : theme.palette.gray.dark1,
}));
const StyledChevronRight = styled(ChevronRight, { shouldForwardProp: shouldForwardProp('disabled') })<ChevronProps>(({ disabled, theme }) => ({
  cursor: disabled ? 'default' : 'pointer',
  color: disabled ? theme.palette.gray.light1 : theme.palette.gray.dark1,
}));

const StyledPage = styled('div', { shouldForwardProp: shouldForwardProp('active') })<PageProps>(({ active, theme }) => ({
  color: active ? theme.palette.sentiment.primary.main : theme.palette.gray.main,
  cursor: 'pointer',
  fontFamily: active ? theme.fonts.semiBold : theme.fonts.regular,
}));

type PageProps = {
  active: boolean;
};

type ChevronProps = {
  disabled: boolean;
} & React.ComponentProps<typeof ChevronLeft>;